<script lang="ts">
	/**
	 * Tooltip component for inline hover help
	 *
	 * @example
	 * ```svelte
	 * <Tooltip text="This field is required">
	 *   <input type="text" />
	 * </Tooltip>
	 * <Tooltip text="This is a helpful message">
	 *   <Fa icon={faHelpingHands} />
	 * </Tooltip>
	 * ```
	 */

	import { onDestroy } from 'svelte';
	import { fade } from 'svelte/transition';
	import { generateId } from '$lib/shared/lib';

	interface Props {
		text: string;
		placement?: 'top' | 'bottom' | 'left' | 'right';
		delay?: number; // milliseconds before showing tooltip
		width?: string;
		variant?: 'standard' | 'gray';
		disabled?: boolean;
		id?: string;
		cursor?: string;
		children?: import('svelte').Snippet;
	}

	let {
		text,
		placement = 'top',
		delay = 200,
		width = 'w-64',
		variant = 'standard',
		disabled = false,
		id = generateId('tooltip'),
		cursor = 'cursor-pointer',
		children
	}: Props = $props();

	let showTooltip = $state(false);
	let timeoutId: number | undefined;

	const placementClasses = {
		top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
		bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
		left: 'right-full top-1/2 -translate-y-1/2 mr-2',
		right: 'left-full top-1/2 -translate-y-1/2 ml-2'
	};

	const arrowClasses = {
		top: 'top-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent',
		bottom:
			'bottom-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent',
		left: 'left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent',
		right:
			'right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent'
	};

	const arrowColorClasses = {
		standard: {
			top: 'border-t-surface-800 dark:border-t-surface-100',
			bottom: 'border-b-surface-800 dark:border-b-surface-100',
			left: 'border-l-surface-800 dark:border-l-surface-100',
			right: 'border-r-surface-800 dark:border-r-surface-100'
		},
		gray: {
			top: 'border-t-white',
			bottom: 'border-b-white',
			left: 'border-l-white',
			right: 'border-r-white'
		}
	};

	const variantClasses = {
		standard: {
			tooltip: 'bg-surface-900-100 text-white dark:text-black shadow-lg'
		},
		gray: {
			tooltip: 'bg-white text-gray-900 border-gray-300 shadow-lg'
		}
	};

	function handleMouseEnter() {
		if (disabled) return;

		timeoutId = window.setTimeout(() => {
			showTooltip = true;
		}, delay);
	}

	function handleMouseLeave() {
		if (timeoutId) {
			clearTimeout(timeoutId);
			timeoutId = undefined;
		}
		showTooltip = false;
	}

	function handleFocus() {
		if (disabled) return;
		showTooltip = true;
	}

	function handleBlur() {
		showTooltip = false;
	}

	function handleTouchStart() {
		if (disabled) return;
		showTooltip = true;
	}

	function handleTouchEnd() {
		// Delay hiding to allow user to read the tooltip
		setTimeout(() => {
			showTooltip = false;
		}, 3000);
	}

	onDestroy(() => {
		if (timeoutId) {
			clearTimeout(timeoutId);
		}
	});
</script>

<div
	class="relative inline-block {cursor}"
	onmouseenter={handleMouseEnter}
	onmouseleave={handleMouseLeave}
	onfocus={handleFocus}
	onblur={handleBlur}
	ontouchstart={handleTouchStart}
	ontouchend={handleTouchEnd}
	role="presentation"
>
	<div aria-describedby={showTooltip ? id : undefined}>
		{@render children?.()}
	</div>

	<!-- Tooltip -->
	{#if showTooltip && !disabled}
		<div
			{id}
			role="tooltip"
			transition:fade={{ duration: 150 }}
			class="absolute z-50 rounded-lg px-3 py-2 text-center text-sm whitespace-normal {width} {placementClasses[
				placement
			]} {variantClasses[variant].tooltip} pointer-events-none"
		>
			{text}

			<!-- Arrow -->
			<div
				class="absolute h-0 w-0 border-4 {arrowClasses[placement]} {arrowColorClasses[variant][
					placement
				]}"
				aria-hidden="true"
			></div>
		</div>
	{/if}
</div>
